import { useState } from "react";
import axios from "axios";
import { ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { LOGO, NAV, CONTACT } from "@/data/site";
import { Icon } from "@/components/Icon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = ({ onJoin }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list", { description: "Follow us. Stay informed. Grow together." });
      setEmail("");
    } catch {
      toast.error("Could not subscribe", { description: "Please check the email address and try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative bg-[color:var(--kmg-deep)] border-t border-[rgba(111,227,155,0.18)] grain" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <img src={LOGO} alt="KMG Digi Platform" className="h-14 w-auto rounded-sm" />
            <p className="font-editorial italic text-2xl mt-8 max-w-sm leading-snug">
              Empowering farmers. Connecting ecosystems. Driving sustainable agriculture.
            </p>
            <button
              data-testid="footer-join-btn"
              onClick={onJoin}
              className="mt-8 rounded-full border border-[color:var(--kmg-lime)] px-7 py-3 text-xs font-mono-label uppercase tracking-[0.18em] text-[color:var(--kmg-lime)] hover:bg-[color:var(--kmg-lime)] hover:text-[color:var(--kmg-forest)]"
              style={{ transition: "background-color .4s ease, color .4s ease" }}
            >
              Get Started
            </button>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">Sections</p>
            <ul className="mt-6 space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  <a href={n.href} className="text-sm text-[color:var(--kmg-bone-dim)] underline-grow hover:text-[color:var(--kmg-lime)]" style={{ transition: "color .35s ease" }}>
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">Stay informed</p>
            <form onSubmit={subscribe} className="mt-6" data-testid="newsletter-form">
              <div className="flex border-b border-[rgba(244,241,230,0.28)] focus-within:border-[color:var(--kmg-lime)]" style={{ transition: "border-color .4s ease" }}>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@farm.co.za"
                  data-testid="newsletter-email-input"
                  className="flex-1 bg-transparent py-3 text-sm outline-none placeholder:text-[color:var(--kmg-bone-dim)] placeholder:opacity-60"
                />
                <button type="submit" disabled={busy} data-testid="newsletter-submit-btn" className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-[color:var(--kmg-lime)] disabled:opacity-50">
                  {busy ? "…" : "Send"}
                </button>
              </div>
            </form>

            <div className="flex items-center gap-3 mt-8">
              {CONTACT.socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.name}
                  data-testid={`footer-social-${s.name.toLowerCase()}`}
                  className="grid place-items-center w-9 h-9 rounded-full border border-[rgba(111,227,155,0.25)] text-[color:var(--kmg-bone-dim)] hover:text-[color:var(--kmg-lime)] hover:border-[color:var(--kmg-lime)]"
                  style={{ transition: "color .35s ease, border-color .35s ease" }}
                >
                  <Icon name={s.icon} size={15} />
                </a>
              ))}
            </div>

            <p className="text-xs mt-8 text-[color:var(--kmg-bone-dim)] opacity-75 leading-relaxed">
              {[CONTACT.headOfficePhone, ...CONTACT.phones].join(" · ")}
              <br />
              {CONTACT.email} · {CONTACT.website}
            </p>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-[rgba(244,241,230,0.14)] flex flex-col sm:flex-row items-center justify-between gap-5">
          <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-[color:var(--kmg-bone-dim)] text-center sm:text-left">
            © {new Date().getFullYear()} KMG Farm · KMG Digi Platform — Plant the seed and feed the people
          </p>
          <button
            data-testid="back-to-top-btn"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-2 font-mono-label text-[10px] uppercase tracking-[0.18em] text-[color:var(--kmg-lime)]"
          >
            Back to top <ArrowUp size={13} />
          </button>
        </div>
      </div>
    </footer>
  );
};
